// ProcessFlowPage.tsx
// AIAG-VDA compliant process flow diagram editor.
// CC violations addressed by extracting all sub-components (CC ≤ 15 each).

import React from 'react';
import ReactFlow, {
    Background, Controls, MiniMap, Panel, ReactFlowProvider, SelectionMode,
} from 'reactflow';
import 'reactflow/dist/style.css';
import clsx from 'clsx';
import {
    Trash2, RotateCcw, LayoutDashboard, ChevronDown, Cog, Search,
    ArrowRight, Database, GitBranch, Circle, BarChart3, Shield, Save,
    FileText, Link2, FolderOpen, Download, Upload, FileJson,
    FileSpreadsheet, Undo2, Redo2, Copy, Clipboard, Image as ImageIcon,
    FileImage, Printer, ListTree,
} from 'lucide-react';
import ProcessFlowNode from '../components/ProcessFlow/ProcessFlowNode';
import ProcessFlowEdge from '../components/ProcessFlow/ProcessFlowEdge';
import { PROCESS_FLOW_TEMPLATES } from '../components/ProcessFlow/ProcessFlowTemplates';
import { ProcessStepType, STEP_TYPE_COLORS } from '../types/processFlow';
import StructureTreeView from '../components/StructureTreeView';
import PropertiesPanel from '../components/ProcessFlow/PropertiesPanel';
import { useProcessFlowEditor } from '../hooks/useProcessFlowEditor';

// ── node/edge type registrations ──────────────────────────────────────────────
const nodeTypes = { processFlowNode: ProcessFlowNode };
const edgeTypes = { processFlowEdge: ProcessFlowEdge };

// ── Helper type alias for the editor return type ──────────────────────────────
type EditorState = ReturnType<typeof useProcessFlowEditor>;

// ── Load-flow dropdown ────────────────────────────────────────────────────────
function LoadMenu({ editor, isDark }: { editor: EditorState; isDark: boolean }) {
    const menuCls = clsx('absolute top-full right-0 mt-1 w-64 rounded-lg shadow-xl border py-1 z-50', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200');
    const btnCls = clsx('flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors', isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700');
    return (
        <div className="relative" ref={editor.loadMenuRef}>
            <button onClick={() => editor.setShowLoadMenu(!editor.showLoadMenu)} className={btnCls}>
                <FolderOpen size={16} /> Load <ChevronDown size={14} className={clsx('transition-transform', editor.showLoadMenu && 'rotate-180')} />
            </button>
            {editor.showLoadMenu && (
                <div className={menuCls}>
                    <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase">Saved Flows</div>
                    {editor.savedFlows.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-slate-500 italic">No saved flows found</div>
                    ) : editor.savedFlows.map(flow => (
                        <div key={flow.id} className={clsx('w-full text-left px-4 py-2 text-sm flex items-center justify-between group transition-colors cursor-pointer', isDark ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')} onClick={() => editor.handleLoadFlow(flow)}>
                            <span className="truncate flex-1">{flow.name}</span>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-400">{new Date(flow.updatedAt).toLocaleDateString()}</span>
                                <button onClick={e => editor.handleDeleteFlow(e, flow)} className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-opacity" title="Delete flow"><Trash2 size={14} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Export dropdown ───────────────────────────────────────────────────────────
function ExportMenu({ editor, isDark }: { editor: EditorState; isDark: boolean }) {
    const itemCls = clsx('w-full text-left px-4 py-2 text-sm flex items-center gap-3 transition-colors', isDark ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50');
    return (
        <div className="relative" ref={editor.exportMenuRef}>
            <button onClick={() => editor.setShowExportMenu(!editor.showExportMenu)} className={clsx('flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors', isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700')}>
                <Download size={16} /> Export <ChevronDown size={14} className={clsx('transition-transform', editor.showExportMenu && 'rotate-180')} />
            </button>
            {editor.showExportMenu && (
                <div className={clsx('absolute top-full right-0 mt-1 w-48 rounded-lg shadow-xl border py-1 z-50', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase">Data Formats</div>
                    <button onClick={editor.handleExportJSON} className={itemCls}><FileJson size={16} className="text-blue-500" /> Export as JSON</button>
                    <button onClick={editor.handleExportCSV} className={itemCls}><FileSpreadsheet size={16} className="text-green-500" /> Export as CSV</button>
                    <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase border-t border-slate-200 dark:border-slate-700 mt-1">Image Formats</div>
                    <button onClick={editor.handleExportPNG} className={itemCls}><ImageIcon size={16} className="text-purple-500" /> Export as PNG</button>
                    <button onClick={editor.handleExportSVG} className={itemCls}><FileImage size={16} className="text-orange-500" /> Export as SVG</button>
                </div>
            )}
        </div>
    );
}

// ── Template dropdown ─────────────────────────────────────────────────────────
function TemplateMenu({ editor, isDark }: { editor: EditorState; isDark: boolean }) {
    return (
        <div className="relative" ref={editor.templateMenuRef}>
            <button onClick={() => editor.setShowTemplateMenu(!editor.showTemplateMenu)} className={clsx('flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors', isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700')}>
                <FileText size={16} /> Templates <ChevronDown size={14} className={clsx('transition-transform', editor.showTemplateMenu && 'rotate-180')} />
            </button>
            {editor.showTemplateMenu && (
                <div className={clsx('absolute top-full right-0 mt-1 w-64 rounded-lg shadow-xl border py-1 z-50', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase">Load Template</div>
                    {Object.entries(PROCESS_FLOW_TEMPLATES).map(([key, template]) => (
                        <button key={key} onClick={() => editor.handleLoadTemplate(key)} className={clsx('w-full text-left px-4 py-2 text-sm flex items-center gap-3 transition-colors', isDark ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}>
                            <LayoutDashboard size={16} className="text-primary-500" />
                            <div><div className="font-medium">{template.name}</div><div className="text-xs text-slate-500">{template.description}</div></div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Toolbar action strip ──────────────────────────────────────────────────────
function ToolbarActions({ editor, isDark }: { editor: EditorState; isDark: boolean }) {
    const iconBtn = (active: boolean) => clsx('p-2 rounded transition-colors', active ? isDark ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600' : 'text-slate-400 cursor-not-allowed');
    const copyBtn = clsx('p-2 rounded transition-colors', isDark ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600');
    const canSave = editor.canEdit || editor.canCreate;
    return (
        <div className="flex items-center gap-2">
            <LoadMenu editor={editor} isDark={isDark} />
            <ExportMenu editor={editor} isDark={isDark} />
            <button onClick={editor.handleImportClick} className={clsx('flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors', isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700')} title="Import from JSON">
                <Upload size={16} /> Import
            </button>
            <input ref={editor.fileInputRef} type="file" accept=".json" onChange={editor.handleFileImport} className="hidden" />
            <TemplateMenu editor={editor} isDark={isDark} />

            <div className="flex items-center border-l border-slate-200 dark:border-slate-700 ml-2 pl-2">
                <button onClick={editor.handleUndo} disabled={editor.historyIndex <= 0} className={iconBtn(editor.historyIndex > 0)} title="Undo (Ctrl+Z)"><Undo2 size={18} /></button>
                <button onClick={editor.handleRedo} disabled={editor.historyIndex >= editor.history.length - 1} className={iconBtn(editor.historyIndex < editor.history.length - 1)} title="Redo (Ctrl+Y)"><Redo2 size={18} /></button>
            </div>
            <div className="flex items-center border-l border-slate-200 dark:border-slate-700 ml-2 pl-2">
                <button onClick={editor.handleCopy} className={copyBtn} title="Copy selected (Ctrl+C)"><Copy size={18} /></button>
                <button onClick={editor.handlePaste} disabled={!editor.clipboard} className={clsx('p-2 rounded transition-colors relative', editor.clipboard ? copyBtn : 'text-slate-400 cursor-not-allowed')} title="Paste (Ctrl+V)">
                    <Clipboard size={18} />{editor.clipboard && <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary-500 rounded-full" />}
                </button>
            </div>
            <button onClick={editor.handleAutoLayout} className={clsx('p-2 rounded transition-colors ml-2', isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-600')} title="Auto Layout"><RotateCcw size={18} /></button>
            <button onClick={editor.handlePrint} className={clsx('p-2 rounded transition-colors', isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-600')} title="Print Diagram"><Printer size={18} /></button>
            <button onClick={() => editor.setShowStructureTree(!editor.showStructureTree)} className={clsx('p-2 rounded transition-colors', editor.showStructureTree ? 'bg-purple-500 text-white' : isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-600')} title="Toggle Structure Tree (AIAG-VDA)"><ListTree size={18} /></button>
            <button onClick={editor.handleExtract} className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors bg-emerald-500 hover:bg-emerald-600 text-white" title="Extract nodes to Golden Thread Analysis"><Link2 size={16} /> Extract</button>
            <button onClick={editor.handleSave} disabled={editor.isSaving || !canSave} className={clsx('flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors', canSave ? 'bg-primary-500 hover:bg-primary-600 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed')} title={!canSave ? 'No permission to save' : 'Save flow'}>
                <Save size={16} /> {editor.isSaving ? 'Saving...' : 'Save'}
            </button>
        </div>
    );
}

// ── Node palette sidebar ───────────────────────────────────────────────────────
const PALETTE_ITEMS = [
    { type: ProcessStepType.OPERATION, icon: <Cog size={18} />, label: 'Op' },
    { type: ProcessStepType.INSPECTION, icon: <Search size={18} />, label: 'Insp' },
    { type: ProcessStepType.TRANSPORT, icon: <ArrowRight size={18} />, label: 'Trans' },
    { type: ProcessStepType.STORAGE, icon: <Database size={18} />, label: 'Store' },
    { type: ProcessStepType.DECISION, icon: <GitBranch size={18} />, label: 'Dec' },
    { type: ProcessStepType.SPC_CONTROL, icon: <BarChart3 size={18} />, label: 'SPC' },
    { type: ProcessStepType.POKA_YOKE, icon: <Shield size={18} />, label: 'P-Y' },
    { type: ProcessStepType.START_END, icon: <Circle size={18} />, label: 'End' },
] as const;

function NodePalette({ editor, isDark }: { editor: EditorState; isDark: boolean }) {
    return (
        <aside className={clsx('w-16 border-r flex flex-col items-center py-4 gap-2', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="text-[10px] font-bold text-slate-400 uppercase mb-2">Add</div>
            {PALETTE_ITEMS.map(({ type, icon, label }) => {
                const colors = STEP_TYPE_COLORS[type];
                return (
                    <button key={type} onClick={() => editor.handleAddNode(type)} className="w-12 h-12 rounded-lg flex flex-col items-center justify-center gap-0.5 transition-all hover:scale-110 border-2 hover:shadow-md" style={{ backgroundColor: colors.bg, borderColor: colors.border, color: colors.text }} title={`Add ${type}`}>
                        {icon}<span className="text-[8px] font-bold">{label}</span>
                    </button>
                );
            })}
        </aside>
    );
}

// ── Canvas legend panel ────────────────────────────────────────────────────────
const LEGEND_TYPES = [ProcessStepType.OPERATION, ProcessStepType.INSPECTION, ProcessStepType.TRANSPORT, ProcessStepType.STORAGE, ProcessStepType.DECISION, ProcessStepType.SPC_CONTROL] as const;

function CanvasLegend({ isDark }: { isDark: boolean }) {
    return (
        <Panel position="bottom-left" className={clsx('p-3 rounded-lg border shadow-sm text-xs', isDark ? 'bg-slate-800/90 border-slate-700' : 'bg-white/90 border-slate-200')}>
            <h4 className={clsx('font-bold mb-2 border-b pb-1', isDark ? 'text-white border-slate-600' : 'text-slate-700 border-slate-200')}>Process Symbols</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {LEGEND_TYPES.map(type => {
                    const colors = STEP_TYPE_COLORS[type];
                    return (
                        <div key={type} className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded border-2" style={{ backgroundColor: colors.bg, borderColor: colors.border }} />
                            <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>{type}</span>
                        </div>
                    );
                })}
            </div>
        </Panel>
    );
}

// ── Main editor (lean render — all logic in hook) ─────────────────────────────
function ProcessFlowEditorInner() {
    const editor = useProcessFlowEditor();
    const { isDark } = editor;

    return (
        <div className={clsx('h-full flex flex-col', isDark ? 'bg-slate-900' : 'bg-slate-50')}>
            {/* Header */}
            <header className={clsx('h-14 border-b flex items-center justify-between px-4 shrink-0', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                <div className="flex items-center gap-4">
                    <input type="text" value={editor.flowName} onChange={e => editor.setFlowName(e.target.value)}
                        className={clsx('text-lg font-bold bg-transparent border-b border-transparent hover:border-slate-300 focus:border-primary-500 focus:outline-none transition-colors px-1', isDark ? 'text-white' : 'text-slate-800')} />
                    <span className="text-xs text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">AIAG-VDA Step 2-3</span>
                </div>
                <ToolbarActions editor={editor} isDark={isDark} />
            </header>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden">
                <NodePalette editor={editor} isDark={isDark} />

                <div className={clsx('flex flex-1', editor.showStructureTree && 'gap-2')}>
                    {/* Canvas */}
                    <div className={clsx('transition-all relative', editor.showStructureTree ? 'flex-[2]' : 'flex-1')} style={{ minHeight: '500px' }}>
                        <ReactFlow
                            nodes={editor.nodes} edges={editor.edges}
                            onNodesChange={editor.onNodesChange} onEdgesChange={editor.onEdgesChange}
                            onConnect={editor.onConnect} onNodeClick={editor.onNodeClick}
                            onEdgeClick={editor.onEdgeClick} onPaneClick={editor.onPaneClick}
                            nodeTypes={nodeTypes} edgeTypes={edgeTypes}
                            fitView selectionOnDrag selectionMode={SelectionMode.Partial}
                            multiSelectionKeyCode="Shift" deleteKeyCode="Delete"
                            className={isDark ? 'bg-slate-900' : 'bg-slate-50'}
                        >
                            <Background color={isDark ? '#475569' : '#94a3b8'} gap={20} size={1} />
                            <Controls className={clsx(isDark ? '!bg-slate-800 !border-slate-700' : '!bg-white !border-slate-200')} />
                            <MiniMap className={clsx(isDark ? '!bg-slate-800 !border-slate-700' : '!bg-white !border-slate-200')} zoomable pannable nodeColor={isDark ? '#64748b' : '#e2e8f0'} />
                            <CanvasLegend isDark={isDark} />
                        </ReactFlow>
                    </div>

                    {/* Structure Tree */}
                    {editor.showStructureTree && (
                        <div className="flex-1 pr-2 overflow-hidden">
                            <StructureTreeView
                                processItems={editor.processItems}
                                processSteps={editor.nodes.filter(n => n.type === 'processFlowNode').map(n => ({
                                    id: n.id, stepNumber: n.data.stepNumber || n.id, stepName: n.data.label,
                                    stepType: n.data.type as any, description: n.data.description || '',
                                    position: n.position, linkedPfmeaIds: [],
                                    workElementIds: editor.workElements.filter(we => we.processStepId === n.id).map(we => we.id),
                                    parentProcessItemId: 'pi-1',
                                }))}
                                workElements={editor.workElements}
                                isDark={isDark}
                                onAddProcessItem={editor.handleAddProcessItem}
                                onAddProcessStep={() => editor.handleAddNode(ProcessStepType.OPERATION)}
                                onAddWorkElement={editor.handleAddWorkElement}
                                onEditItem={editor.handleEditItem}
                                onDeleteItem={editor.handleDeleteItem}
                            />
                        </div>
                    )}

                    {/* Properties Panel */}
                    {(editor.selectedNode || editor.selectedEdge) && (
                        <PropertiesPanel
                            selectedNode={editor.selectedNode || null}
                            selectedEdge={editor.selectedEdge || null}
                            onUpdateNode={editor.handleUpdateNode}
                            onUpdateEdge={editor.handleUpdateEdge}
                            onDeleteNode={editor.handleDeleteNode}
                            onDeleteEdge={editor.handleDeleteEdge}
                            workElements={editor.workElements}
                            onAddWorkElement={editor.handleAddWorkElement}
                            onUpdateWorkElement={editor.handleUpdateWorkElement}
                            onDeleteWorkElement={editor.handleDeleteWorkElement}
                            onSave={editor.handleSave}
                            isSaving={editor.isSaving}
                            canEdit={editor.canEdit || editor.canCreate}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

// ── App entry point ───────────────────────────────────────────────────────────
const ProcessFlowPage: React.FC = () => (
    <ReactFlowProvider>
        <ProcessFlowEditorInner />
    </ReactFlowProvider>
);

export default ProcessFlowPage;
